import { useState } from 'react';
import { toast } from 'sonner';
import { sarApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Loader2 } from 'lucide-react';

/**
 * HelpMeButton — flags this request for a Precious Data admin when someone
 * is stuck: overdue with no clear next step, a reply that doesn't make
 * sense, anything. It shares this request's status and messages (never the
 * documents that come back — those stay end-to-end encrypted) so an admin
 * can actually help, not just acknowledge the flag.
 */
const HelpMeButton = ({ sar, onUpdate }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [sending, setSending] = useState(false);

  if (!sar) return null;
  const pending = !!sar.help_requested_at && !sar.help_resolved_at;

  const submit = async () => {
    setSending(true);
    try {
      await sarApi.requestHelp(sar.id, reason.trim() || null);
      toast.success("Sent. A Precious Data admin has been notified.");
      setOpen(false);
      setReason('');
      onUpdate?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send that. Try again in a moment.');
    } finally {
      setSending(false);
    }
  };

  if (pending) {
    return (
      <div className="rounded-lg bg-ground px-6 py-5" data-testid="sar-help-pending">
        <p className="text-[15px] font-medium text-ink">We're looking into this</p>
        <p className="mt-1 text-sm text-ink-55 max-w-[60ch]">
          You asked for help with this request. A Precious Data admin has been notified and can
          see what has happened with it so far.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-lg bg-ground px-6 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <p className="text-[15px] font-medium text-ink">Not sure what to do next?</p>
          <p className="mt-1 text-sm text-ink-55 max-w-[52ch]">
            If this request is stuck, overdue, or a reply doesn't make sense, ask a Precious Data
            admin to take a look.
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          className="shrink-0 bg-surface hover:bg-inset"
          onClick={() => setOpen(true)}
          data-testid="sar-help-me-btn"
        >
          Help me with this
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ask a Precious Data admin for help</DialogTitle>
            <DialogDescription>
              This shares this request's status and messages with a Precious Data admin — not any
              documents you've received, those stay private to you — so someone can help you work
              out what to do next.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <label htmlFor="sar-help-reason" className="text-[15px] font-semibold text-ink">
              What's going on? <span className="font-normal text-ink-55">(optional)</span>
            </label>
            <Textarea
              id="sar-help-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="For example: it's overdue and I'm not sure what to do next, or I don't understand what they sent back."
              className="bg-surface border-surface"
              data-testid="sar-help-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={sending}>Cancel</Button>
            <Button onClick={submit} disabled={sending} data-testid="sar-help-submit">
              {sending && <Loader2 className="w-4 h-4 animate-spin mr-2" aria-hidden="true" />}
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default HelpMeButton;
